import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import unorm from 'unorm';
import Graph from './graphTable';
import Pie from '../common/new_graph/pie';

// function created for severity
const colorSeverityItem = severety => {
  // condition to define the color as defined in the api
  if (severety == null) {
    return 'gris';
  } else if (severety.toUpperCase() === 'BAD' || severety.toUpperCase() === 'WORSE') {
    return 'red';
  } else if (severety.toUpperCase() === 'GOOD' || severety.toUpperCase() === 'BETTER') {
    return 'green';
  }
  return 'yellow';
};

const colorSeverityText = severety => {
  // condition to define the color as defined in the api
  if (severety == null) {
    return '';
  } else if (severety.toUpperCase() === 'BAD' || severety.toUpperCase() === 'WORSE') {
    return 'baja respecto al período anterior';
  } else if (severety.toUpperCase() === 'GOOD' || severety.toUpperCase() === 'BETTER') {
    return 'mejora respecto al período anterior';
  }
  return '';
};

// function performed to obtain the color of the pie chart according to the settings
const colorSeverity = (arr, type) => {
  // formatting process to be able to find the element in the array
  let str = type;
  str = unorm.nfd(str).replace(/[\u0300-\u036f]/g, '');
  str = str.toLowerCase();

  const obj = arr.find(o => o.key === str);

  return obj ? obj.color : '';
};

// function performed to translate footer elements with setting configurations
const translate = (arr, type, language) => {
  // formatting process to be able to find the element in the array
  let str = type;
  str = unorm.nfd(str).replace(/[\u0300-\u036f]/g, '');
  str = str.toLowerCase();
  const obj = arr.find(o => o.key === str);
  return obj[`${language}Name`];
};

// function that helps to render the trend of the cells
const renderTrend = (trend, actual, previous, differenceValue, language) => {
  if (trend.toUpperCase() === 'BETTER' && differenceValue > 0) {
    return (
      <span className="green tool">
        <i className="fas fa-arrow-up" />
        <div className="info">
          <h3 className={colorSeverityItem(trend.toUpperCase())}>
            {differenceValue} {colorSeverityText(trend.toUpperCase())}
          </h3>
          <p>
            {language ? language.nfa.now : ''} ({moment(actual.start).format('DD-MM')} al{' '}
            {moment(actual.end).format('DD-MM')}):{' '}
            <strong className={colorSeverityItem(actual.classification)}>{actual.value}</strong>
          </p>
          <p>
            {language ? language.nfa.before : ''} (
            {previous ? moment(previous.start).format('DD-MM') : '-'}{' '}
            {language ? language.nfa.to : ''}{' '}
            {previous ? moment(previous.end).format('DD-MM') : '-'}){' '}
            <strong className={colorSeverityItem(previous ? previous.classification : null)}>
              {previous ? previous.value : '0'}
            </strong>
          </p>
        </div>
      </span>
    );
  } else if (trend.toUpperCase() === 'WORSE' && differenceValue > 0) {
    return (
      <span className="red tool">
        <i className="fas fa-arrow-down" />
        <div className="info">
          <h3 className={colorSeverityItem(trend.toUpperCase())}>
            {differenceValue} {colorSeverityText(trend.toUpperCase())}
          </h3>
          <p>
            {language.nfa.now} ({moment(actual.start).format('DD-MM')} al{' '}
            {moment(actual.end).format('DD-MM')}):{' '}
            <strong className={colorSeverityItem(actual.classification)}>{actual.value}</strong>
          </p>
          <p>
            {language.nfa.before} ({previous ? moment(previous.start).format('DD-MM') : '-'}{' '}
            {language.nfa.to} {previous ? moment(previous.end).format('DD-MM') : '-'}){' '}
            <strong className={colorSeverityItem(previous ? previous.classification : null)}>
              {previous ? previous.value : '0'}
            </strong>
          </p>
        </div>
      </span>
    );
  }
  return <span />;
};

// function that helps to render the trend of the cells
const renderTrendFail = (trend, actual, previous, differenceValue, language) => {
  if (trend.toUpperCase() === 'BETTER' && differenceValue > 0) {
    return (
      <span className="green tool">
        <i className="fas fa-arrow-down" />
        <div className="info">
          <h3 className={colorSeverityItem(trend.toUpperCase())}>
            {differenceValue} falla(s) menos respecto al periodo anterior
          </h3>
          <p>
            {language ? language.nfa.now : ''} ({moment(actual.start).format('DD-MM')} al{' '}
            {moment(actual.end).format('DD-MM')}):{' '}
            <strong className={colorSeverityItem(actual.classification)}>{actual.value}</strong>
          </p>
          <p>
            {language ? language.nfa.before : ''} (
            {previous ? moment(previous.start).format('DD-MM') : '-'}{' '}
            {language ? language.nfa.to : ''}{' '}
            {previous ? moment(previous.end).format('DD-MM') : '-'}){' '}
            <strong className={colorSeverityItem(previous ? previous.classification : null)}>
              {previous ? previous.value : '0'}
            </strong>
          </p>
        </div>
      </span>
    );
  } else if (trend.toUpperCase() === 'WORSE' && differenceValue > 0) {
    return (
      <span className="red tool">
        <i className="fas fa-arrow-up" />
        <div className="info">
          <h3 className={colorSeverityItem(trend.toUpperCase())}>
            {differenceValue} falla(s) más respecto al periodo anterior
          </h3>
          <p>
            {language.nfa.now} ({moment(actual.start).format('DD-MM')} al{' '}
            {moment(actual.end).format('DD-MM')}):{' '}
            <strong className={colorSeverityItem(actual.classification)}>{actual.value}</strong>
          </p>
          <p>
            {language.nfa.before} ({previous ? moment(previous.start).format('DD-MM') : '-'}{' '}
            {language.nfa.to} {previous ? moment(previous.end).format('DD-MM') : '-'}){' '}
            <strong className={colorSeverityItem(previous ? previous.classification : null)}>
              {previous ? previous.value : '0'}
            </strong>
          </p>
        </div>
      </span>
    );
  }
  return <span />;
};

const Table = props => (
  <div className="module-table top">
    <table>
      <thead>
        <tr>
          <th className="pd-left left">
            <a
              role="button"
              data-type="tagalias"
              tabIndex={0}
              onClick={props.handleSort}
              className="th100"
            >
              {props.language.filter[props.main.toLowerCase()]}{' '}
              <i className="fas fa-arrows-alt-v" />
            </a>
          </th>
          <th>
            <a role="button" data-type="averageScore" tabIndex={0} onClick={props.handleSort}>
              {props.language.summary.tableTitle.formAvgRange} <i className="fas fa-arrows-alt-v" />
            </a>
          </th>
          <th className="th250">
            <a>{props.language.summary.tableTitle.evolutionScore} </a>
          </th>
          <th>
            <a role="button" data-type="inspections" tabIndex={0} onClick={props.handleSort}>
              {props.language.summary.tableTitle.inspections} <i className="fas fa-arrows-alt-v" />
            </a>
          </th>
          <th>
            <a role="button" data-type="fails" tabIndex={0} onClick={props.handleSort}>
              {props.language.summary.tableTitle.fails} <i className="fas fa-arrows-alt-v" />
            </a>
          </th>
          <th className="th250">
            <a role="button" data-type="severityFails" tabIndex={0} onClick={props.handleSort}>
              {props.language.summary.tableTitle.severityFailsColor}{' '}
              <i className="fas fa-arrows-alt-v" />
            </a>
          </th>
        </tr>
      </thead>
      <tbody>
        {props.datos.map((item, i) => {
          return (
            <tr key={`${i}`}>
              {/* Name */}
              <td className="first">
                <span
                  className={`${colorSeverityItem(item.averageScore.actual.classification)} font16`}
                />{' '}
                {item.name}
              </td>
              {/* Average score */}
              <td className="center">
                <div className="avg">
                  {
                    <span className={colorSeverityItem(item.averageScore.actual.classification)}>
                      {item.averageScore.actual.value}
                    </span>
                  }
                  {renderTrend(
                    item.averageScore.trend,
                    item.averageScore.actual,
                    item.averageScore.previous,
                    item.averageScore.differenceValue,
                    props.language.summary
                  )}

                  <span className="absolute">
                    <p className={colorSeverityItem(item.averageScore.trend)}>
                      {item.averageScore.differenceValue !== 0
                        ? `${item.averageScore.differenceValue}`
                        : ''}
                    </p>
                  </span>
                </div>
              </td>
              {/* Evolution Score */}
              <td>
                <div className="flex align-center">
                  <div className="graph-table">
                    {!props.loadingTable && <Graph data={item.evolutionScore} id={`graph-${i}`} />}
                  </div>
                  <div>
                    {item.evolutionScore.length > 0 ? (
                      <p
                        className={colorSeverityItem(
                          item.evolutionScore[item.evolutionScore.length - 1].classification
                        )}
                      >
                        {' '}
                        {item.evolutionScore[item.evolutionScore.length - 1].averageScoreDay}
                      </p>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              </td>
              {/* Inspections */}
              <td className="center font18">{item.inspections}</td>
              {/* Fails */}
              <td className="center">
                <div className="avg">
                  {
                    <span className={colorSeverityItem(item.fails.actual.classification)}>
                      {item.fails.actual.value}
                    </span>
                  }
                  {renderTrendFail(
                    item.fails.trend,
                    item.fails.actual,
                    item.fails.previous,
                    item.fails.differenceValue,
                    props.language.summary
                  )}

                  <span className="absolute">
                    <p className={colorSeverityItem(item.fails.trend)}>
                      {item.fails.differenceValue !== 0 ? `${item.fails.differenceValue}` : ''}
                    </p>
                  </span>
                </div>
              </td>
              <td>
                <div className="container-pie flex">
                  <div>
                    <Pie
                      size={30}
                      data={item.severityFails}
                      width={100}
                      height={60}
                      animation={false}
                      translate
                      name="severityType"
                      value="value"
                      colors={props.severityFailsColor}
                      language={props.languageFormat}
                    />
                  </div>
                  <div>
                    <ul className="list-pie">
                      {item.severityFails.map(val => (
                        <li key={val.severityType}>
                          <span
                            style={{
                              background: colorSeverity(props.severityFailsColor, val.severityType)
                            }}
                          />
                          {translate(
                            props.severityFailsColor,
                            val.severityType,
                            props.languageFormat
                          )}{' '}
                          {val.value}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    {props.loadingTable && (
      <div className="loading-table">
        <h6>
          <i className="fas fa-spinner fa-spin" />
        </h6>
      </div>
    )}
    {props.pag >= props.count.pages && (
      <div className="loading-table">
        <h6>{props.language.summary.nfa.noData}</h6>
      </div>
    )}
  </div>
);
Table.propTypes = {
  pag: PropTypes.number.isRequired,
  count: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  loadingTable: PropTypes.bool.isRequired,
  main: PropTypes.string.isRequired,
  language: PropTypes.oneOfType([PropTypes.object]).isRequired,
  datos: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
    PropTypes.array
  ]).isRequired
};
export default Table;



// WEBPACK FOOTER //
// ./source/client/components/summary_truck/table.jsx